import { AutoColumn } from 'components/Column'
import IntroCard from 'components/IntroCard'
import { RowBetween, AutoRow } from 'components/Row'
import { DynamicSection, ScrollablePage, Wrapper } from 'pages/AddLiquidity/styled'
import AppBody from 'pages/AppBody'
import React, { useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { ExternalLink, TYPE } from 'theme'
import InputTextPanel from './InputTextPanel'
import { StyledTabButton } from 'pages/FarmPool'
import { useDerivedMintInfo, useMintActionHandlers, useMintState } from 'state/mint/hooks'
import { ETHER, Token, TokenAmount } from '@uniswap/sdk-core'
import { Field } from 'state/mint/actions'
import { Text } from 'rebass'

import { maxAmountSpend } from 'utils/maxAmountSpend'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import CurrencyInputPanelWithoutCurrency from 'components/CurrencyInputPanelWithoutCurrency'
import { ButtonPrimary, ButtonRadioChecked, ButtonSecondary } from 'components/Button'
import { FeeAmount } from '@uniswap/v3-sdk'
import { useTransactionAdder } from 'state/transactions/hooks'
import { useWalletModalToggle } from 'state/application/hooks'
import { calculateGasMargin, getContract, getEtherscanLink } from 'utils'
import { LAUNCHER_ADDRESS } from '@hyperswap/addresses'
import { abi as Launcher_ABI } from '../../abis/hyperswap_launcher.json'
import { parseUnits } from 'ethers/lib/utils'
import { useToken } from 'hooks/Tokens'
import { PairState, useV2Pair } from 'hooks/useV2Pairs'
import { useAddUserToken, usePairAdder } from 'state/user/hooks'
import Modal from 'components/Modal'
import { useActiveWeb3React } from 'hooks'
import { Helmet } from 'react-helmet'

const Tabs = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  border-radius: 3rem;
  justify-content: space-evenly;
`
const ResponsiveText = styled(TYPE.label)`
  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 12px;
  `};
`

const Launcher = () => {
  const { independentField, typedValue, otherTypedValue } = useMintState()
  const { account, library, chainId } = useActiveWeb3React()

  const toggleWalletModal = useWalletModalToggle()
  const theme = useContext(ThemeContext)

  const [name, setName] = React.useState('')
  const [symbol, setSymbol] = React.useState('')
  const [supply, setSupply] = React.useState('1000')
  const [isV2LP, setIsV2LP] = React.useState(true)
  const [tokenB, setTokenB] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [feeAmount, setFeeAmount] = React.useState<FeeAmount | undefined>(undefined)

  const [tokenAddress, setTokenAddress] = React.useState<null | string>('')
  const [tokenCreatedName, setTokenCreatedName] = React.useState<null | string>(null)
  const [addTokenFlag, setAddTokenFlag] = React.useState<boolean>(false)

  const searchToken = useToken(tokenAddress || '')
  const [pairState, pair] = useV2Pair(ETHER, searchToken ?? undefined)
  const [modalIsOpen, setModalIsOpen] = React.useState(false)
  const addPair = usePairAdder()
  const addToken = useAddUserToken()

  React.useEffect(() => {
    if (tokenAddress && searchToken && searchToken instanceof Token && addTokenFlag) {
      addToken(searchToken as Token)

      if (pairState === PairState.EXISTS && pair) {
        addPair(pair)
      }

      setAddTokenFlag(false)
    }
  }, [searchToken, addTokenFlag, pair, pairState, tokenAddress, addToken, addPair])

  const {
    dependentField,
    currencies,

    currencyBalances,
    parsedAmounts,

    noLiquidity,
  } = useDerivedMintInfo(ETHER ?? undefined, undefined)

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: noLiquidity ? otherTypedValue : parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const { onFieldAInput } = useMintActionHandlers(noLiquidity)

  const maxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const alphanumericRegex = /^[a-zA-Z0-9]*$/

    if (value.length <= 10 && alphanumericRegex.test(value)) {
      setName(value)
    }
  }

  const handleChangeSymbol = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const alphanumericRegex = /^[a-zA-Z0-9]*$/

    if (value.length <= 10 && alphanumericRegex.test(value)) {
      setSymbol(value)
    }
  }
  const handleChangeSupply = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    // Remove any non-numeric characters
    let numericValue = value.replace(/[^0-9]/g, '')

    // Remove leading zeros
    if (numericValue.length > 0) {
      numericValue = numericValue.replace(/^0+/, '')
    }

    // Limit the length to 15 digits
    if (numericValue.length > 15) {
      numericValue = numericValue.substring(0, 15)
    }

    setSupply(numericValue)
  }

  const isDisabled =
    loading === true ||
    name.length === 0 ||
    symbol.length === 0 ||
    supply === '0' ||
    supply.length === 0 ||
    tokenB.length === 0 ||
    parseFloat(tokenB) <= 0 ||
    formattedAmounts[Field.CURRENCY_A].length === 0 ||
    parseFloat(formattedAmounts[Field.CURRENCY_A]) <= 0 ||
    parseFloat(tokenB) > parseFloat(supply) ||
    (!isV2LP && feeAmount === undefined)

  const addTransaction = useTransactionAdder()

  async function onAdd() {
    if (!chainId || !library || !account) return
    const launcher = getContract(LAUNCHER_ADDRESS, Launcher_ABI, library, account)

    const estimate = isV2LP ? launcher.estimateGas.createTokenAndAddLPV2 : launcher.estimateGas.createTokenAndAddLPV3
    const method = isV2LP ? launcher.createTokenAndAddLPV2 : launcher.createTokenAndAddLPV3
    setTokenAddress(null)
    setTokenCreatedName(null)
    const args = isV2LP
      ? [name, symbol, parseUnits(supply, 18).toString(), account, parseUnits(tokenB, 18).toString()]
      : [name, symbol, parseUnits(supply, 18).toString(), account, feeAmount, parseUnits(tokenB, 18).toString()]

    const value = parseUnits(formattedAmounts[Field.CURRENCY_A], 18)

    setLoading(true)

    await estimate(...args, value ? { value } : {})
      .then((estimatedGasLimit) =>
        method(...args, {
          ...(value ? { value } : {}),
          gasLimit: calculateGasMargin(estimatedGasLimit),
        }).then(async (response: any) => {
          addTransaction(response, {
            summary: `Launch token $${symbol}`,
          })

          // Wait for the transaction to be mined
          const receipt = await response.wait()

          // Fetch the transaction using the transaction hash
          const transaction = await library.getTransaction(receipt.transactionHash)
          // Ensure the transaction initiator is your account
          if (transaction.from.toLowerCase() === account.toLowerCase()) {
            // Look through the logs for the TokenCreated event
            const event = receipt.events?.find((event: any) => event.event === 'TokenCreated')

            if (event) {
              const tokenAddress = event.args._address
              setTokenAddress(tokenAddress)
              setTokenCreatedName(name)
              setAddTokenFlag(true)

              setName('')
              setSymbol('')
              setSupply('1000')
              setTokenB('')

              if (tokenAddress) {
                setModalIsOpen(true)
              }
            }
          } else {
            console.warn('Transaction initiator does not match the current account')
          }

          setLoading(false)
        })
      )
      .catch((error) => {
        setLoading(false)
        setTokenAddress(null)
        setTokenCreatedName(null)
        // we only care if the error is something _other_ than the user rejected the tx
        if (error?.code !== 4001) {
          console.error(error)
          setLoading(false)
        }
      })
  }

  return (
    <>
      <Helmet>
        <title>Token Launcher | HyperSwap Exchange on Hyperliquid</title>
        <meta
          name="description"
          content="Effortlessly create tokens with HyperSwap's Token Launcher. Launch tokens in two clicks with built-in support for V2 and V3 liquidity pools."
        />
        <meta
          name="keywords"
          content="HyperSwap token launcher, create tokens, liquidity management, V2 pools, V3 pools, Hyperliquid blockchain, DeFi tools"
        />
        <meta property="og:title" content="Token Launcher | HyperSwap Exchange on Hyperliquid" />
        <meta
          property="og:description"
          content="Launch your tokens in just two clicks using HyperSwap's Token Launcher, complete with liquidity pool integration for V2 and V3."
        />
        <meta property="og:url" content={`${window.location.origin}/#/launcher`} />
      </Helmet>
      <div style={{ maxWidth: '480px' }}>
        <IntroCard variant={1} key="Launcher">
          <h1>Token Launcher</h1>
          <p>
            The Token Launcher is a tool designed to create tokens effortlessly in just two clicks, with integrated
            support for V2 and V3 liquidity pools to ensure efficient token and liquidity management.
          </p>
        </IntroCard>
      </div>

      <ScrollablePage>
        <AppBody>
          <Tabs>
            <RowBetween style={{ padding: '1rem 1rem 0 1rem', justifyContent: 'center' }}>
              <TYPE.mediumHeader fontWeight={500} fontSize={20}>
                Launch a Token
              </TYPE.mediumHeader>
            </RowBetween>
          </Tabs>
          <Wrapper>
            <AutoColumn gap="12px">
              <RowBetween paddingBottom="20px" marginTop="12px">
                <TYPE.label>Select Liquidity Type</TYPE.label>
              </RowBetween>
              <div style={{ maxWidth: '300px' }}>
                <Tabs style={{ justifyContent: 'flex-start' }}>
                  <StyledTabButton isActive={isV2LP} onClick={() => setIsV2LP(true)}>
                    V2 Position
                  </StyledTabButton>
                  <StyledTabButton isActive={!isV2LP} onClick={() => setIsV2LP(false)}>
                    V3 Position
                  </StyledTabButton>
                </Tabs>
              </div>
              <RowBetween paddingBottom="20px" marginTop="12px">
                <TYPE.label>Token Info</TYPE.label>
              </RowBetween>
              <InputTextPanel
                value={name}
                handleChange={handleChangeName}
                label={'Token Name'}
                error={false}
                disabled={false}
                placeholder="HYPE"
              />
              <InputTextPanel
                value={symbol}
                handleChange={handleChangeSymbol}
                label={'Token Symbol'}
                error={false}
                disabled={false}
                placeholder="HYPE"
              />
              <InputTextPanel
                value={supply}
                handleChange={handleChangeSupply}
                label={'Token Supply'}
                error={false}
                disabled={false}
                placeholder="10000000"
              />

              {!isV2LP && (
                <AutoColumn gap="16px" style={{ marginTop: '12px' }}>
                  <DynamicSection gap="md">
                    <TYPE.label>Select Fee Tier</TYPE.label>
                    <TYPE.main fontSize={14} fontWeight={400} style={{ marginBottom: '.5rem', lineHeight: '125%' }}>
                      Select a pool type based on your preferred liquidity provider fee.
                    </TYPE.main>
                    <AutoRow gap="2px">
                      <ButtonRadioChecked
                        width="49%"
                        active={feeAmount === FeeAmount.LOWER}
                        onClick={() => setFeeAmount(FeeAmount.LOWER)}
                        style={{ background: '#212121' }}
                      >
                        <AutoColumn gap="sm" justify="flex-start">
                          <ResponsiveText>0.01% fee</ResponsiveText>
                          <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                            Best for very stable pairs.
                          </TYPE.main>
                        </AutoColumn>
                      </ButtonRadioChecked>
                      <ButtonRadioChecked
                        width="49%"
                        active={feeAmount === FeeAmount.LOW}
                        onClick={() => setFeeAmount(FeeAmount.LOW)}
                        style={{ background: '#212121' }}
                      >
                        <AutoColumn gap="sm" justify="flex-start">
                          <ResponsiveText>0.05% fee</ResponsiveText>
                          <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                            Best for stable pairs.
                          </TYPE.main>
                        </AutoColumn>
                      </ButtonRadioChecked>
                      <ButtonRadioChecked
                        width="49%"
                        active={feeAmount === FeeAmount.MEDIUM}
                        onClick={() => setFeeAmount(FeeAmount.MEDIUM)}
                        style={{ background: '#212121' }}
                      >
                        <AutoColumn gap="sm" justify="flex-start">
                          <ResponsiveText>0.3% fee</ResponsiveText>
                          <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                            Best for most pairs.
                          </TYPE.main>
                        </AutoColumn>
                      </ButtonRadioChecked>
                      <ButtonRadioChecked
                        width="49%"
                        active={feeAmount === FeeAmount.HIGH}
                        onClick={() => setFeeAmount(FeeAmount.HIGH)}
                        style={{ background: '#212121' }}
                      >
                        <AutoColumn gap="sm" justify="flex-start">
                          <ResponsiveText>1% fee</ResponsiveText>
                          <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                            Best for exotic pairs.
                          </TYPE.main>
                        </AutoColumn>
                      </ButtonRadioChecked>
                    </AutoRow>
                  </DynamicSection>
                </AutoColumn>
              )}

              <RowBetween paddingBottom="20px" marginTop="12px">
                <TYPE.label>Deposit Amounts</TYPE.label>
              </RowBetween>
              <CurrencyInputPanel
                value={formattedAmounts[Field.CURRENCY_A]}
                onUserInput={onFieldAInput}
                onMax={() => {
                  onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
                }}
                showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
                currency={currencies[Field.CURRENCY_A]}
                id="launcher-tokenA"
                showCommonBases
                smallTokenName
              />
              <CurrencyInputPanelWithoutCurrency
                value={tokenB}
                onUserInput={setTokenB}
                onMax={() => setTokenB(supply.length > 0 ? supply : '0')}
                maxValue={supply}
                id="launcher-tokenB"
                showCommonBases
                smallTokenName
                showMaxButton={true}
                label={name.length > 0 ? name : 'Token'}
              />
              <RowBetween style={{ marginTop: '12px' }}>
                {account ? (
                  <ButtonPrimary disabled={isDisabled} onClick={() => onAdd()}>
                    Launch
                  </ButtonPrimary>
                ) : (
                  <ButtonSecondary onClick={() => toggleWalletModal}>Connect a wallet</ButtonSecondary>
                )}
              </RowBetween>
            </AutoColumn>
          </Wrapper>
        </AppBody>
      </ScrollablePage>
      {tokenAddress && tokenAddress?.length > 0 && (
        <Modal isOpen={modalIsOpen} onDismiss={() => setModalIsOpen(false)} maxHeight={90}>
          <Wrapper style={{ minWidth: '100%', maxWidth: '100%' }}>
            <div style={{ padding: '60px 0', display: 'flex', justifyContent: 'center' }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="90"
                height="90"
                viewBox="0 0 24 24"
                fill="none"
                stroke={theme.primary1}
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon icon-tabler icons-tabler-outline icon-tabler-rosette-discount-check"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M5 7.2a2.2 2.2 0 0 1 2.2 -2.2h1a2.2 2.2 0 0 0 1.55 -.64l.7 -.7a2.2 2.2 0 0 1 3.12 0l.7 .7c.412 .41 .97 .64 1.55 .64h1a2.2 2.2 0 0 1 2.2 2.2v1c0 .58 .23 1.138 .64 1.55l.7 .7a2.2 2.2 0 0 1 0 3.12l-.7 .7a2.2 2.2 0 0 0 -.64 1.55v1a2.2 2.2 0 0 1 -2.2 2.2h-1a2.2 2.2 0 0 0 -1.55 .64l-.7 .7a2.2 2.2 0 0 1 -3.12 0l-.7 -.7a2.2 2.2 0 0 0 -1.55 -.64h-1a2.2 2.2 0 0 1 -2.2 -2.2v-1a2.2 2.2 0 0 0 -.64 -1.55l-.7 -.7a2.2 2.2 0 0 1 0 -3.12l.7 -.7a2.2 2.2 0 0 0 .64 -1.55v-1" />
                <path d="M9 12l2 2l4 -4" />
              </svg>
            </div>
            <AutoColumn gap="12px" justify={'center'}>
              <Text fontWeight={500} fontSize={20} textAlign="center">
                Token Successfully created!
              </Text>
              {chainId && (
                <ExternalLink href={getEtherscanLink(chainId, tokenAddress ?? '', 'address')}>
                  <Text fontWeight={500} fontSize={14} color={theme.primary1}>
                    View ${tokenCreatedName} on Explorer
                  </Text>
                </ExternalLink>
              )}
              <ButtonPrimary onClick={() => setModalIsOpen(false)} style={{ margin: '20px 0 0 0' }}>
                <Text fontWeight={500} fontSize={20}>
                  Close
                </Text>
              </ButtonPrimary>
            </AutoColumn>
          </Wrapper>
        </Modal>
      )}
    </>
  )
}
export default Launcher
